/**
 * PortalLoginPage
 *
 * Standalone page (no auth required) rendered when a client visits:
 *   /#/portal-invite/{token}   ← WhatsApp / copied link
 *   /#/portal-login            ← direct bookmark / returning client
 *
 * Three modes:
 *   1. sign-in     — email + password (default / returning clients)
 *   2. magic-link  — "send me a login link" (first-time / forgot password)
 *   3. link-sent   — confirmation screen after magic link email is sent
 */
import React, { useState } from 'react';
import { Scale, Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, Loader, Send } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

export default function PortalLoginPage() {
  const [mode,      setMode]      = useState('sign-in'); // 'sign-in' | 'magic-link' | 'link-sent'
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  /* ── Sign in with email + password ── */
  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) {
      setError(
        err.message.includes('Invalid login') || err.message.includes('invalid_credentials')
          ? 'Incorrect email or password. Try "Send me a login link" below if this is your first time.'
          : err.message
      );
    }
    // On success AppWrapper picks up SIGNED_IN and routes to PortalApp automatically.
  };

  /* ── Send magic link ── */
  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setError(''); setLoading(true);
    // Redirect to root — Supabase appends its own hash tokens so we can't
    // include a hash fragment here. AppWrapper detects the portal client and
    // routes to PortalApp automatically after the session is established.
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setMode('link-sent');
  };

  /* ── Shared input style ── */
  const inputCls = `w-full px-4 py-2.5 rounded-xl border text-sm transition outline-none
    bg-white border-slate-200 text-slate-900 placeholder-slate-400
    focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-indigo-600 items-center justify-center mb-4 shadow-lg">
            <Scale size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Client Portal</h1>
          <p className="text-indigo-300 text-sm mt-1">Access your case files, invoices &amp; receipts</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* ── Mode: link sent ── */}
          {mode === 'link-sent' && (
            <div className="p-8 text-center space-y-4">
              <div className="inline-flex w-14 h-14 rounded-full bg-emerald-100 items-center justify-center mx-auto">
                <CheckCircle size={28} className="text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Check your email</h2>
              <p className="text-sm text-slate-500">
                We sent a login link to <strong className="text-slate-700">{email}</strong>.
                Click the link in that email to access your portal — it expires in 1 hour.
              </p>
              <p className="text-xs text-slate-400">Can't find it? Check your spam folder.</p>
              <button
                onClick={() => { setMode('sign-in'); setError(''); }}
                className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium mt-2"
              >
                <ArrowLeft size={14} /> Back to sign in
              </button>
            </div>
          )}

          {/* ── Mode: sign-in ── */}
          {mode === 'sign-in' && (
            <div className="p-8 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Sign in</h2>
                <p className="text-sm text-slate-500 mt-0.5">Use the email your firm has on file for you</p>
              </div>

              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Email address</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={`${inputCls} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Password</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      type={showPw ? 'text' : 'password'} required
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      className={`${inputCls} pl-10 pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
                )}

                <button
                  type="submit" disabled={loading}
                  className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader size={15} className="animate-spin" /> Signing in…</> : 'Sign in'}
                </button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">or</span></div>
              </div>

              <button
                onClick={() => { setMode('magic-link'); setError(''); }}
                className="w-full py-2.5 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-sm font-medium transition flex items-center justify-center gap-2"
              >
                <Send size={14} /> Send me a login link
              </button>

              <p className="text-center text-xs text-slate-400">
                First time here? Use "Send me a login link" — no password needed.
              </p>
            </div>
          )}

          {/* ── Mode: magic-link ── */}
          {mode === 'magic-link' && (
            <div className="p-8 space-y-5">
              <div>
                <button
                  onClick={() => { setMode('sign-in'); setError(''); }}
                  className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mb-4"
                >
                  <ArrowLeft size={13} /> Back
                </button>
                <h2 className="text-lg font-semibold text-slate-900">Get a login link</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Enter your email and we'll send you a one-click link to access your portal — no password required.
                </p>
              </div>

              <form onSubmit={handleMagicLink} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Email address</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={`${inputCls} pl-10`}
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
                )}

                <button
                  type="submit" disabled={loading}
                  className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader size={15} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send login link</>}
                </button>
              </form>

              <p className="text-center text-xs text-slate-400">
                Use the same email address your firm registered you with.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-indigo-400/60 text-xs mt-6">
          Secure client portal · Powered by DocketFlow
        </p>
      </div>
    </div>
  );
}
